import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/api';
import { ArrowLeft } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import PasswordStrengthIndicator, { validatePassword } from '../components/PasswordStrengthIndicator';
import { toast } from 'sonner';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Token mancante');
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    const { isValid } = validatePassword(password);
    if (!isValid) {
      toast.error('La password non rispetta i requisiti di sicurezza');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', {
        token,
        new_password: password,
      });
      setSuccess(true);
      toast.success('Password aggiornata!');
      setTimeout(() => navigate('/login'), 2500);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Errore durante il reset';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="px-4 pt-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-primary transition-colors" data-testid="back-home-link">
          <ArrowLeft size={16} /> Torna alla home
        </Link>
      </div>
      <div className="flex items-center justify-center px-4 py-16">
        <Card className="w-full max-w-md p-8 neumorphism rounded-2xl" data-testid="reset-password-form">
          <div className="text-center mb-8">
            <h1 className="font-heading font-semibold text-3xl text-foreground mb-2">
              Nuova Password
            </h1>
            <p className="text-foreground/70">
              Inserisci la tua nuova password
            </p>
          </div>

          {success ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <span className="text-4xl">✅</span>
              </div>
              <p className="text-foreground/80">
                Password aggiornata con successo!
              </p>
              <p className="text-sm text-foreground/60">
                Verrai reindirizzato al login...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password">Nuova Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <PasswordStrengthIndicator password={password} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Conferma Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  data-testid="confirm-password-input"
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-destructive" data-testid="password-mismatch">
                    Le password non coincidono
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full rounded-full"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? 'Aggiornamento...' : 'Reimposta Password'}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-primary font-medium hover:underline">
                  Torna al login
                </Link>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
